import { DashboardContent } from 'src/layouts/dashboard';
import { useNotify, useTranslate } from 'react-admin';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { Stack, Box, Button, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, IconButton, Paper, Tooltip, Divider, TablePagination } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useEffect, useState } from 'react';
import useManually from './useManually';
import { addManually, deleteManually } from 'src/pages/admin/services/whitelistService';

export interface WhitelistEntry {
  userId: string;
  walletAddress: string;
  manualNotes: string;
  registeredAt: string;
  email: string;
  proxyWalletAddress: string;
  status: string;
  unlockSource: string;
  unlockSourceLockedAt: number;
  ticketsTotal: number;
  cumulativeVolumeUsdc: string;
  validReferralCount: number;
  firstTradeAt: any;
  selectedAt: any;
}

const isValidEthereumAddress = (address: string): boolean => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

const View = () => {
  const translate = useTranslate();
  const notify = useNotify();

  const [proxyWalletAddress, setProxyWalletAddress] = useState('');
  const [remark, setRemark] = useState('');
  const [proxyWalletAddressError, setProxyWalletAddressError] = useState<string>('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { retrieve, records: whitelist, total } = useManually();

  useEffect(() => {
    retrieve(page + 1, rowsPerPage);
  }, [page, rowsPerPage, retrieve]);

  const handleAdd = async () => {
    if (!isValidEthereumAddress(proxyWalletAddress.trim())) {
      setProxyWalletAddressError('请输入有效的代理钱包地址 (0x + 40位十六进制字符)');
      return;
    }
    setProxyWalletAddressError('');

    try {
      const response = await addManually('', '', remark.trim(), proxyWalletAddress.trim());
      if (response.status === 200) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
        setProxyWalletAddress('');
        setRemark('');
        retrieve(page + 1, rowsPerPage);
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
    } catch (error: any) {
      console.log('error: ', error);
      const message = error?.response?.data?.message || translate('whitelist.failed');
      notify(message, { type: 'error', autoHideDuration: 5000 });
    }
  };

  const handleDelete = async(id: string, walletAddress: string, proxyWalletAddress: string) => {
    try {
      const response = await deleteManually(id, walletAddress, proxyWalletAddress)
      if (response.status === 200) {
        notify(translate('whitelist.success'), { type: 'success', autoHideDuration: 1000 });
      } else {
        notify(translate('whitelist.failed'), { type: 'error', autoHideDuration: 1000 });
      }
      retrieve(page + 1, rowsPerPage)
    } catch (error: any) {
      console.log("error: ", error)
      notify(error?.response?.data?.message || translate('whitelist.failed'), { type: 'error', autoHideDuration: 5000 });
    }
  };

  const exportToCSV = () => {
    const headers = [
      translate('whitelist.manually.address'),
      translate('whitelist.manually.remarkOptional'),
      translate('whitelist.manually.timestamp'),
    ];
    const rows = whitelist.map((entry) => [
      entry.walletAddress,
      entry.manualNotes,
      entry.registeredAt.toLocaleString(),
    ]);
    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', 'manual_whitelist.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardContent>
      <PermissionGuard resource="whitelist-manually" action="read" fallback={<ErrorPage403 />}>
      <Box sx={{ p: 3 }}>
        <Card sx={{ width: '100%' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom fontWeight="medium">
              {translate('whitelist.manually.title')}
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <TextField
                  label={translate('whitelist.manually.proxyWalletAddress')}
                  value={proxyWalletAddress}
                  onChange={(e) => setProxyWalletAddress(e.target.value)}
                  error={!!proxyWalletAddressError}
                  helperText={proxyWalletAddressError || translate('whitelist.manually.addressValidation')}
                  placeholder="0x..."
                  sx={{ flex: 2 }}
                />
                <TextField
                  label={translate('whitelist.manually.remarkOptional')}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder={translate('whitelist.manually.remardDesc')}
                  helperText=" "
                  sx={{ flex: 1 }}
                />
                <PermissionGuard resource="whitelist-manually" action="create" fallback={null}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    disabled={!proxyWalletAddress.trim()}
                    sx={{ height: 40, mt: '4px', whiteSpace: 'nowrap' }}
                  >
                    {translate('whitelist.manually.add')}
                  </Button>
                </PermissionGuard>
              </Stack>
            </Paper>

            <Divider sx={{ mb: 3 }} />

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6">
                {translate('whitelist.manually.addedWhitelist')} ({whitelist.length})
              </Typography>
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={exportToCSV}
                disabled={whitelist.length === 0}
              >
                {translate('whitelist.exportCSV')}
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'background.default' }}>
                    <TableCell>{translate('whitelist.manually.address')}</TableCell>
                    <TableCell>{translate('whitelist.manually.remark')}</TableCell>
                    <TableCell>{translate('whitelist.manually.timestamp')}</TableCell>
                    <TableCell align="center">{translate('whitelist.manually.action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {whitelist.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          {translate('whitelist.manually.noWhitelistedAddresses')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    whitelist.map((entry) => (
                      <TableRow key={entry.userId} hover>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{entry.proxyWalletAddress}</TableCell>
                        <TableCell>{entry.manualNotes || '-'}</TableCell>
                        <TableCell>{new Date(Number(entry.registeredAt)).toLocaleString()}</TableCell>
                        <TableCell align="center">
                          <PermissionGuard resource="whitelist-manually" action="delete" fallback={null}>
                            <Tooltip title={translate('whitelist.manually.remove')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(entry.userId, entry.walletAddress, entry.proxyWalletAddress)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </PermissionGuard>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      </PermissionGuard>
    </DashboardContent>
  );
};

export default View;
